/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useAppData } from '@/hooks/useAppData';
import Skeleton from '@/components/Skeleton/Skeleton';
import Price from '@/components/AZCustomComponent/Price';
import cx from 'classnames';
import azCommonStyles from '@/theme/globals.module.scss';
import StrikeThroughPriceWrapper from '@/components/AZCustomComponent/StrikethroughPricing';
import { useFeatureFlag } from '@/features/globalConfig';
import styles from './styles.module.scss';
import { Label } from '@/features/i18n';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { countryCodes } from '@/constants/locale';
import { useShowSkeletonOrNoPrice } from './useShowSkeletonOrNoPrice';
import PriceV2 from '@/components/AZCustomComponent/Price/PriceV2';
import { useLabels } from '@/hooks/useLabels';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

type Props = {
  discountedPrice: string | null;
  updatedDiscountedPrice: number;
  configurableSku: boolean;
  retailPrice: string[];
  retailPriceInt: number;
  itemQuantity: number;
};

const labelMap = {
  lblStartingAt: 'label_starting_at',
  lblAnd: 'label_MyAccountMyProfile_rewards_And',
  lblPrice: 'label_the_price_of_item',
  lblDollars: 'label_shelf_resultsList_price_dollars',
  lblCents: 'label_shelf_resultsList_price_cents',
};

const PriceInfoPopover = ({
  discountedPrice,
  updatedDiscountedPrice,
  configurableSku,
  retailPrice,
  retailPriceInt,
  itemQuantity,
}: Props) => {
  const { appData } = useAppData();
  const { isFetchingLocation } = appData;
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const locale = useLocale();
  const labels = useLabels(labelMap);
  const { isLoading, isNoPrice } = useShowSkeletonOrNoPrice({
    isFetchingLocation,
    retailPriceInt,
    updatedDiscountedPrice,
    configurableSku,
  });
  const isStrikethrough = discountedPrice && parseFloat(discountedPrice) !== retailPriceInt;

  const ariaPriceOfItem = labels.lblPrice;
  const dollarsLabel = labels.lblDollars;
  const centsLabel = labels.lblCents;

  // Helper function to create screen reader friendly price text
  const getScreenReaderPrice = (price: number): string => {
    const priceString = price.toFixed(2);
    const [dollars, cents] = priceString.split('.');

    if (dollars === '0' && cents === '00') {
      return `0 ${dollarsLabel}`;
    }
    if (dollars === '0') {
      return `${cents} ${centsLabel}`;
    }
    if (cents === '00') {
      return `${dollars} ${dollarsLabel}`;
    }

    return `${dollars} ${dollarsLabel} ${labels.lblAnd} ${cents} ${centsLabel}`;
  };

  const getAriaLabel = () => {
    if (strikeThroughPricingEnabled && isStrikethrough) {
      const discountedPriceText = getScreenReaderPrice(updatedDiscountedPrice);
      const originalPriceText = getScreenReaderPrice(retailPriceInt);
      return `${ariaPriceOfItem} ${discountedPriceText}. ${labels.lblStartingAt} ${originalPriceText}`;
    } else {
      const priceToUse = configurableSku ? parseFloat(retailPrice.join('.')) : retailPriceInt;
      return `${ariaPriceOfItem} ${getScreenReaderPrice(priceToUse)}`;
    }
  };

  if (isNoPrice) {
    return <NoPrice />;
  }

  return isLoading ? (
    getSkeletonOrNoPrice(locale)
  ) : (
    <>
      {strikeThroughPricingEnabled && isStrikethrough ? (
        <div
          className={cx(styles.strikethroughPriceWrapper, {
            [styles.alignStart]: isProductDetailsPageV2Enabled,
          })}
        >
          <span
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: '0',
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              borderWidth: '0',
            }}
          >
            {getAriaLabel()}
          </span>
          <div className={cx(azCommonStyles['az-caption'])} aria-hidden="true">
            {isProductDetailsPageV2Enabled ? (
              <PriceV2
                priceType="strikethrough"
                dealPrice={updatedDiscountedPrice?.toFixed(2).split('.')}
              />
            ) : (
              <Price
                priceType="strikethrough"
                dealPrice={updatedDiscountedPrice?.toFixed(2).split('.')}
              />
            )}
          </div>
          <div className={styles.marginLeft} aria-hidden="true">
            <StrikeThroughPriceWrapper
              price={updatedDiscountedPrice}
              wasPrice={retailPriceInt}
              quantity={itemQuantity}
            />
          </div>
        </div>
      ) : (
        <div className={cx(azCommonStyles['az-caption'])}>
          <span
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: '0',
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              borderWidth: '0',
            }}
          >
            {getAriaLabel()}
          </span>
          <div aria-hidden="true">
            {isProductDetailsPageV2Enabled ? (
              <PriceV2
                priceType="strikethrough"
                isConfigurableSku={configurableSku}
                dealPrice={retailPrice}
              />
            ) : (
              <Price
                priceType="strikethrough"
                isConfigurableSku={configurableSku}
                dealPrice={retailPrice}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const getSkeletonOrNoPrice = (locale: Locale) => {
  if (locale === countryCodes.mx) {
    return <NoPrice />;
  } else {
    return <Skeleton className={styles.priceSkeleton} width={70} height={38} animation={'wave'} />;
  }
};

const NoPrice = () => (
  <div data-testid="price-fragment">
    <span className={cx(azCommonStyles['az-title-5-heavy'], styles.noPrice)}>
      <Label label="label_shelf_resultsList_price_not_available" />
    </span>
  </div>
);

export default PriceInfoPopover;
